import {SharedColors} from "@fluentui/theme";
import {Stack, Text} from "@fluentui/react";
import React, {ReactElement, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {AppParts} from "../enums/AppParts";
import {hr} from "../locales/hr";

export interface ITitleAndContentLayoutProps {
    children?: any;
    title?: keyof typeof hr | string;
    styles?: any;
    pad?: boolean;
    isSubtitle?: boolean;
    lessPadding?: boolean;
    noPadding?: boolean;
    padVertical?: number;
    textStyle?: any;
    error?: string;
    errorAddon?: string;
    errorCallback?: () => void;
    errorBandOnly?: boolean;
    subtitle?: keyof typeof hr | string;
    titleAddon?: string;
    subtitleAddon?: string;
    hasCommandBar?: boolean;
    noMargin?: boolean;
    hrNoMargin?: boolean;
    errorSubtitle?: string;
    smallSubtitle?: boolean;
    rightTitleAddon?: () => ReactElement;
    titleWidget?: () => ReactElement;
    skipRendering?: boolean;
}

export const TitleAndContentLayout = ({
                                          skipRendering,
                                          children,
                                          smallSubtitle,
                                          rightTitleAddon,
                                          titleWidget,
                                          hrNoMargin,
                                          errorAddon,
                                          hasCommandBar,
                                          titleAddon,
                                          errorSubtitle,
                                          noMargin,
                                          subtitleAddon,
                                          subtitle,
                                          title,
                                          styles,
                                          pad,
                                          textStyle,
                                          isSubtitle,
                                          lessPadding,
                                          noPadding,
                                          padVertical,
                                          error,
                                          errorCallback,
                                          errorBandOnly,
                                      }: ITitleAndContentLayoutProps) => {
    const {t} = useTranslation();

    const titlePadding = useMemo(() => {
        let titlePadding: any = {
            paddingLeft: 50,
            paddingTop: 25,
            paddingBottom: 5,
            paddingRight: 50,
            lineBreak: "word",
        };
        if (lessPadding)
            titlePadding = {
                paddingLeft: 20,
                paddingTop: 15,
                paddingBottom: 15,
                paddingRight: 20,
            };
        if (noPadding) titlePadding = {};
        if (textStyle) titlePadding = {...titlePadding, ...textStyle};
        if (hasCommandBar) {
            titlePadding.paddingTop = 0;
        }
        return titlePadding;
    }, [lessPadding, noPadding, textStyle, hasCommandBar]);

    const contentPadding = useMemo(() => {
        let contentPadding: any = {paddingLeft: 50, paddingRight: 50};
        if (lessPadding) contentPadding = {paddingLeft: 20, paddingRight: 20};
        if (noPadding) contentPadding = {paddingTop: 20};
        return contentPadding;
    }, [lessPadding, noPadding]);

    const errorPadding = useMemo(() => {
        const errorPadding = {
            ...titlePadding,
            paddingTop: titlePadding.paddingTop / 2,
            paddingBottom: titlePadding.paddingTop / 2,
            backgroundColor: SharedColors.red10,
            cursor: !!errorCallback ? "pointer" : null,
        };
        return errorPadding;
    }, [titlePadding]);

    const subtitlePadding = useMemo(() => {
        const subtitlePadding: any = {paddingLeft: 50};
        return subtitlePadding;
    }, []);

    if (skipRendering) return children;

    return (
        <Stack
            tokens={errorBandOnly ? {} : {childrenGap: padVertical}}
            style={
                errorBandOnly
                    ? {}
                    : {...(styles ?? {}), marginBottom: noMargin ? 0 : 25}
            }
        >
            <Stack>
                {!errorBandOnly && (
                    <Stack>
                        <Stack horizontal horizontalAlign={"space-between"}>
                            <Stack horizontal>
                                {
                                    <Text
                                        variant={isSubtitle ? "large" : "xLarge"}
                                        styles={{root: titlePadding}}
                                    >
                                        {t(title as string)}
                                        {!!titleAddon ? (!!title ? " " : "") + titleAddon : ""}
                                    </Text>
                                }
                                {titleWidget ? titleWidget() : null}
                            </Stack>
                            {rightTitleAddon ? rightTitleAddon() : null}
                        </Stack>
                        {!!subtitle ? (
                            <Text
                                variant={
                                    isSubtitle
                                        ? smallSubtitle
                                            ? "small"
                                            : "medium"
                                        : smallSubtitle
                                            ? "medium"
                                            : "large"
                                }
                                styles={{root: subtitlePadding}}
                            >
                                {t(subtitle as string)}
                                {!!subtitleAddon ? (!!subtitle ? " " : "") + subtitleAddon : ""}
                            </Text>
                        ) : null}
                    </Stack>
                )}
                {(!!error || !!errorAddon || !!errorSubtitle) && (
                    <Stack
                        styles={{root: errorPadding}}
                        onClick={errorCallback}
                        id={AppParts.WORK_RECORDS_CLOSED_PERIOD}
                    >
                        <Stack horizontal>
                            <Text
                                variant={isSubtitle ? "small" : "large"}
                                styles={{
                                    root: {
                                        color: "white",
                                        fontWeight: "bold",
                                    },
                                }}
                            >
                                {!!error ? error : ""}
                                {!!errorAddon ? (!!error ? " " : "") + errorAddon : ""}
                            </Text>
                        </Stack>
                        {!!errorSubtitle ? (
                            <Text
                                style={{color: "white"}}
                                variant={isSubtitle ? "xSmall" : "small"}
                            >
                                {errorSubtitle}
                            </Text>
                        ) : null}
                    </Stack>
                )}
            </Stack>
            {/*{!errorBandOnly && <hr style={hrNoMargin ? {margin: 0} : undefined}/>}*/}
            {pad ? (
                <div style={errorBandOnly ? {} : contentPadding}>{children}</div>
            ) : (
                children
            )}
        </Stack>
    );
};
